/**
 * ImportReviewBrowser — one verdict-aware, browsable list for an import
 * review (GDN-2178 / ADR-0076).
 *
 * Replaces the summary-screen + separate-conflict-queue split: every staged
 * resource is a row carrying its classify verdict. Undecided conflicts and
 * ambiguous matches sort to the top with inline resolve controls, so decisions
 * are unmissable without walling them off from the rest. Any row expands to
 * inspect its converted data (the operator's convert-confidence check).
 * Filters slice by type / verdict / search.
 *
 * Commit is progressive-trust (GDN-2250/2251): a decided patient row commits
 * individually, and the current verdict filter commits as a cohort — the host
 * supplies the complete per-verdict patient key sets, since the browser only
 * sees the loaded window.
 *
 * Presentational and self-contained: the host feeds rows + counts and handles
 * the decision/commit callbacks. The host sends the collected decisions as
 * the commit's conflict_resolutions and gates whole-session commit until every
 * conflict is decided.
 */
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Pagination,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Chip as UiChip } from '@jotpsych/ui';
import { format } from 'date-fns';

import type { RefTarget } from '../store/api';
import { candidateDisplayName } from './import-review-types';
import type {
  ImportVerdict,
  ReviewDecision,
  ReviewFieldDiff,
  ReviewMatchCandidate,
  ReviewMatchResolution,
  VerdictCounts,
} from './import-review-types';

// ---------------------------------------------------------------------------
// Row contract
// ---------------------------------------------------------------------------

export interface ReviewResourceRow {
  id: string;
  resourceType: string;
  /** Operator-facing label (PHI allowed in UI, never in telemetry). */
  label: string;
  /** Optional secondary line, e.g. a date or source key. */
  sublabel?: string;
  verdict: ImportVerdict;
  /** Short description of the existing record a match was made against. */
  matchedSummary?: string;
  /**
   * Id of the existing record this row matched (patient rows: the patient_id).
   * The browser doesn't know app routes, so it doesn't build a URL itself — a
   * host that wants the matched record linked passes a `patientHref` resolver
   * that turns this into a link target.
   */
  matchedPatientId?: string;
  /** Competing fields (verdict === 'conflict') — the decision. */
  competing?: ReviewFieldDiff[];
  /** Non-competing fills that merge automatically (update / conflict FYI). */
  autoMerge?: ReviewFieldDiff[];
  /** Existing-patient candidates (verdict === 'ambiguous') — the choice. */
  matchCandidates?: ReviewMatchCandidate[];
  /**
   * Whole-record decisions valid for this row, when the host constrains them
   * (GDN-2271). If set, only these `decisionOptions` render — e.g. a parked row
   * with no matched record can't `keep_existing`/`take_import`, so the practice
   * inbox passes `['skip','import_as_new']` and those two buttons are hidden
   * rather than shown and then rejected 409 by the backend. Omitted (the admin
   * host) means every `decisionOptions` entry renders, unchanged.
   */
  allowedDecisions?: ReviewDecision[];
  /**
   * Why an ambiguous row needs a decision: 'identifier_collision' |
   * 'weak_demographic_match' | 'ambiguous_identifier' | 'ambiguous_match'.
   */
  matchBasis?: string;
  /** Converted payload, shown on inspect — the convert-confidence check. */
  data?: Record<string, unknown>;
  /**
   * Row synthesized by the host from the conflict/match feeds because its
   * staged row is outside the current server page. The server never saw
   * these, so in server-browse mode the type/search filters apply to them
   * client-side (a 120k-row import pins 500+ of them — leaving them exempt
   * buried the filtered results; GDN-2247).
   */
  pinned?: boolean;
  /**
   * The owning patient's source key — set on patient rows so a decided row
   * can commit individually (GDN-2251).
   */
  patientKey?: string;
  /**
   * Owning patient's display name for a child row, resolved server-side from
   * the row's refs (GDN-2264), so it shows regardless of the browse page.
   * Lets the operator see whose chart a staged appointment/med/etc. lands on.
   */
  patientLabel?: string;
  /**
   * A child that references no patient but carries a converter-declared name
   * (the title-match didn't resolve): it will NOT link on commit. Rendered as
   * a warning so the operator can act — link manually or re-match later.
   */
  patientUnlinked?: boolean;
  /**
   * The underlying staged row is already committed (GDN-2270 follow-up). Set
   * when browsing under a Committed/All status filter so the row is badged and
   * excluded from the default Pending queue. The classify verdict still shows
   * for context (what it committed as).
   */
  committed?: boolean;
  /**
   * Resources this row depends on (GDN-2376): one entry per ref, each carrying
   * its resolved state (committed / staged / missing). Rendered as a compact
   * per-ref chip row so an operator sees, at a glance, what still blocks this
   * record's commit and can click through to a committed target's page or drill
   * into a staged target's review row.
   */
  refTargets?: RefTarget[];
  /**
   * This row's own committed record id, once it has committed. Distinct from a
   * ref target's `committed_resource_id` — this is the row itself.
   */
  committedResourceId?: string;
}

/**
 * Overridable operator-facing copy (GDN-2271). Every field is optional and
 * defaults to the admin host's current wording, so omitting `copyOverrides`
 * leaves the admin surface byte-for-byte unchanged. A terminal host (the
 * practice "Data import" surface) passes plainer language for the few strings
 * that read as operator jargon there.
 */
export interface ImportReviewCopyOverrides {
  /** Summary header title (default "Import review"). */
  summaryTitle?: string;
  /**
   * The record-count line under the title, given the total record count.
   * Default: "{n} resources · all converted" (or "· N couldn't be read").
   */
  summaryCountLabel?: (count: number) => string;
  /**
   * Bulk "keep existing" button label — a plain string or a function of the
   * open-conflict count. Default: "Keep existing for {n} conflict(s)".
   */
  bulkKeepLabel?: string | ((count: number) => string);
  /**
   * Bulk "take import" button label — a plain string or a function of the
   * open-conflict count. Default: "Take import for {n} conflict(s)".
   */
  bulkTakeLabel?: string | ((count: number) => string);
  /** Conflict section header (default "Conflicting values — choose which to keep"). */
  conflictHeader?: string;
  /** Expanded-row conflict blurb (default `VERDICT_BLURB.conflict`). */
  conflictBlurb?: string;
}

/**
 * Org-review-flag browse filter (GDN-2300): 'all' applies no flag filter,
 * 'flagged' shows only org-review-flagged rows (org_review_needed=true), and
 * 'not_flagged' shows only the rest (org_review_needed=false). Drives the
 * three-way segmented control and maps 1:1 onto the browse's tri-state param.
 */
export type OrgReviewFilterMode = 'all' | 'flagged' | 'not_flagged';

/**
 * Invariant for the org-review segmented control (GDN-2366): never strand the
 * operator on a filter whose own segment is empty. Given the whole-import
 * counts, return the filter that should be active — callers apply it only when
 * it differs from the current value.
 *
 * - 'flagged' with nothing flagged → 'all' (empty view; matches the pre-tri-state
 *   behaviour where the flag chip vanished with nothing to filter to).
 * - 'not_flagged' with nothing un-flagged (e.g. every row flagged) → 'all'.
 * - 'not_flagged' with a full import and nothing flagged yet **stays put** — in
 *   that state "Not flagged" is the entire import, which is exactly the view the
 *   filter exists to give before anything's been flagged. (The old invariant
 *   snapped it back to 'all', making it unreachable.)
 * - 'all', and any filter whose segment is non-empty, are left untouched.
 */
export function reconcileOrgReviewFilter(
  filterMode: OrgReviewFilterMode,
  flaggedTotal: number,
  notFlaggedTotal: number,
): OrgReviewFilterMode {
  if (filterMode === 'flagged' && flaggedTotal === 0) return 'all';
  if (filterMode === 'not_flagged' && notFlaggedTotal === 0) return 'all';
  return filterMode;
}

export interface ImportReviewBrowserProps {
  sourceLabel?: string;
  totalRecords: number;
  totals: VerdictCounts;
  rows: ReviewResourceRow[];
  /** Controlled decisions by row id (uncontrolled if omitted). */
  decisions?: Record<string, ReviewDecision>;
  onDecisionChange?: (id: string, decision: ReviewDecision | null) => void;
  /**
   * Override the conflict decision buttons — both which options appear and
   * their labels. Defaults to the full admin `DECISIONS` vocabulary. A host on
   * a terminal review tier can relabel in plainer language and hide options
   * that don't apply there (e.g. the practice org-review surface trims the set
   * to what a practice admin can act on).
   */
  decisionOptions?: { value: ReviewDecision; label: string }[];
  /**
   * Override operator-jargon copy with plainer wording for a terminal host.
   * Omit to keep the admin defaults (see {@link ImportReviewCopyOverrides}).
   */
  copyOverrides?: ImportReviewCopyOverrides;
  /**
   * Per-row disable signal (GDN-2271). A row whose id is in this set has its
   * conflict decision buttons disabled — e.g. a terminal host that resolves on
   * click disables the row while its resolve is in flight, so a rapid second
   * click can't fire a duplicate mutation. Default: nothing disabled, so the
   * admin host (which stages decisions locally) is unaffected.
   */
  disabledRowIds?: ReadonlySet<string>;
  /** Controlled ambiguous-match resolutions by row id (uncontrolled if omitted). */
  matchResolutions?: Record<string, ReviewMatchResolution>;
  onMatchResolutionChange?: (
    id: string,
    resolution: ReviewMatchResolution | null,
  ) => void;
  /**
   * Progressive-trust commit (GDN-2250/2251). When provided, a decided
   * patient row gets its own Commit button, and the current verdict filter
   * commits as a cohort using the host-supplied complete key sets (the
   * browser only sees the loaded window). The host owns dispatch, gating
   * errors, and refresh.
   */
  commit?: {
    onCommitPatients: (patientKeys: string[]) => void;
    inFlight: boolean;
    /** Complete per-verdict patient key sets across the whole import. */
    patientKeysByVerdict: Partial<Record<ImportVerdict, string[]>>;
  };
  /**
   * Open the source-document preview for a row (GDN-2214). Offered on
   * document_reference rows; the host owns the dialog + fetch.
   */
  onViewDocument?: (rowId: string) => void;
  /**
   * Server-driven browse (GDN-2244, GDN-2287). When provided, the type filter,
   * search box, pagination — and, when wired, the verdict chips — are
   * controlled by the host, which fetches the matching page from the server, so
   * every filter covers the whole import pre-pagination, not a loaded window.
   * With `onVerdictFilterChange` supplied the host no longer merges a full
   * client-side dump of conflicts/ambiguous rows into `rows`; each is reachable
   * by selecting its verdict chip and paging.
   */
  serverBrowse?: {
    typeFilter: string;
    onTypeFilterChange: (type: string) => void;
    search: string;
    onSearchChange: (search: string) => void;
    page: number;
    onPageChange: (page: number) => void;
    perPage: number;
    totalRows: number;
    /** All resource types in the import (page rows won't contain them all). */
    resourceTypes: string[];
    /**
     * Commit-status view (GDN-2270 follow-up): 'pending' (the not-yet-committed
     * review queue), 'committed', or 'all'. Optional — when the host doesn't
     * supply it, the status control is hidden and the queue is whatever the
     * host fetched. Scopes both the row list and the verdict chip counts, so
     * committed rows drop out of the Pending queue while unprocessable and
     * escalated rows stay visible.
     */
    statusFilter?: string;
    onStatusFilterChange?: (status: string) => void;
    /**
     * Verdict filter (GDN-2287). When wired, the classify-verdict chips are
     * server-driven: selecting a verdict re-fetches that verdict's page from
     * the BE (filtered pre-pagination), so the chip spans the whole import. When
     * omitted, verdict falls back to the legacy client-side slice over `rows`.
     */
    verdictFilter?: string;
    onVerdictFilterChange?: (verdict: string) => void;
  };
  /**
   * Persisted shared selection (GDN-2297). When provided, every row gets a
   * leading checkbox, a selection bar shows the total + "Clear all", and a
   * "Selected N" filter chip appears. The set is host-owned and persisted
   * BE-side, shared between this UI and the MCP agent — so `byActor` can read
   * out "12 by agent · 3 by you". Omit to hide selection entirely.
   */
  selection?: {
    /** Selected row ids on the loaded page (drives the checkboxes). */
    selectedIds: ReadonlySet<string>;
    /** Toggle a row's membership; the host persists it. */
    onToggle: (id: string, next: boolean) => void;
    /** Clear the whole session's selection. */
    onClear: () => void;
    /** Total selected across the WHOLE import, not just this page. */
    selectedTotal: number;
    /** Provenance read-out across the whole selection (e.g. agent vs you). */
    byActor?: { label: string; count: number }[];
    /**
     * Server-driven "show only selected" filter (rides the browse the same way
     * type/status/verdict do). Optional — omit to hide the filter chip.
     */
    filterActive?: boolean;
    onFilterChange?: (active: boolean) => void;
  };
  /**
   * Persisted org-review flag (GDN-2300). Replaces the old `escalate` commit
   * decision with an orthogonal, toggleable flag — the exact shape/spirit of
   * `selection`. When provided, every row gets a "Flag for org review" /
   * "Unflag" affordance (with provenance), a summary read-out ("N flagged for
   * org review" + by-actor split), and a server-driven filter chip. Host-owned,
   * persisted BE-side, and shared with the MCP agent (so `byActor` can read out
   * "3 by agent · 1 by you"). Omit to hide org-review entirely.
   */
  orgReview?: {
    /** Flagged row ids on the loaded page (drives the per-row flag state). */
    flaggedIds: ReadonlySet<string>;
    /**
     * Provenance for flagged rows on the loaded page, keyed by row id: the
     * literal "agent" or a user id (null/undefined when unknown). Drives the
     * "flagged by …" per-row read-out.
     */
    flaggedBy?: Record<string, string | null | undefined>;
    /** Toggle a row's flag; the host persists it (true flags, false unflags). */
    onToggle: (id: string, needed: boolean) => void;
    /** Total flagged across the WHOLE import, not just this page. */
    total: number;
    /** Provenance read-out across the whole flagged set (e.g. agent vs you). */
    byActor?: { label: string; count: number }[];
    /**
     * Server-driven org-review-flag filter (rides the browse the same way
     * type/status/verdict/selected do): 'all' (no filter), 'flagged'
     * (org_review_needed=true), or 'not_flagged' (org_review_needed=false).
     * Optional — omit `onFilterModeChange` to hide the filter control.
     */
    filterMode?: OrgReviewFilterMode;
    onFilterModeChange?: (mode: OrgReviewFilterMode) => void;
    /**
     * Total NOT-flagged across the WHOLE import — the count on the 'not_flagged'
     * segment. Parallels `total` (the flagged count).
     */
    notFlaggedTotal?: number;
  };
  onCommit?: () => void;
  /**
   * Show the built-in commit bar. Off when a host owns the commit step (e.g.
   * StagedResourcesSection, which has its own gated Commit button).
   */
  showCommitBar?: boolean;
  /**
   * Show the "Bulk: keep/take for all" actions (GDN-2302). Default on (admin).
   * A terminal host can hide them — e.g. the practice inbox resolves per row
   * and doesn't want a batch action on this surface.
   */
  showBulkActions?: boolean;
  /**
   * Show the per-row verdict/status chip ("Needs review" → "Resolved")
   * (GDN-2302). Default on (admin, where verdict varies row-to-row). A single-
   * verdict host (the practice inbox is all conflicts) can hide the chip, whose
   * repetition is noise there.
   */
  showStatusChips?: boolean;
  /**
   * Show the built-in summary header — title + record count + the verdict
   * filter chips ("All N", "Needs review N") (GDN-2302). Default on (admin). A
   * host that renders its own header and whose queue is single-verdict can hide
   * it to avoid a redundant second header and noise-only chips.
   */
  showSummary?: boolean;
  /**
   * Resolve a row to the URL of the existing record it matched (GDN-2302), so a
   * host can link the matched record to its page (e.g. the practice inbox links
   * `matchedPatientId` to `/patient/:id`). Return `undefined` for rows that
   * shouldn't link (e.g. unmatched). The browser stays route-agnostic — it only
   * renders the anchor when this yields a URL.
   */
  patientHref?: (row: ReviewResourceRow) => string | undefined;
  /**
   * Resolve an ambiguous-match candidate's patient id to the URL of that
   * patient's page (GDN-2344), so the operator can open the existing chart to
   * decide "is this the same person?". Return `undefined` to render no link.
   * Route-agnostic — the browser only renders the anchor when this yields a URL.
   */
  candidateHref?: (patientId: string) => string | undefined;
  /**
   * Resolve a COMMITTED ref target to its Resource Explorer URL (GDN-2376), so a
   * committed dependency chip links to the live record. Return `undefined` to
   * render the chip without a link. Route-agnostic — mirrors `patientHref`.
   */
  resourceHref?: (
    resourceType: string,
    committedResourceId: string,
  ) => string | undefined;
  /**
   * Drill into a STAGED ref target's review row (GDN-2376): the host focuses that
   * row (e.g. by driving its server-browse type filter + search). Omit to render
   * staged chips non-clickable.
   */
  onOpenStagedRef?: (target: RefTarget) => void;
  /**
   * Dependency-aware per-row commit (GDN-2376), for ANY resource type: commit
   * exactly this row now (the BE commits it if its deps are met, else blocks it
   * with a clear ref reason). Patient rows keep their existing progressive-trust
   * Commit button (see `commit`) — the per-row button only renders for
   * non-patient rows, so a row never shows two Commit buttons.
   */
  onCommitRow?: (row: ReviewResourceRow) => void;
  /**
   * Up-front blocker for a row's per-row commit that the ref chips don't cover
   * (GDN-2376) — e.g. an encounter whose required source provider isn't mapped
   * yet, which would otherwise fail server-side on click. Return a short reason
   * to disable the Commit button and show it as the tooltip; return `undefined`
   * to leave it enabled.
   */
  commitRowDisabledReason?: (row: ReviewResourceRow) => string | undefined;
  /**
   * Show the raw "Converted data" key/value dump on expanded rows (GDN-2302) —
   * the operator's convert-confidence check. Default on (admin). A practice
   * host hides it: the diff already carries the decision, and the raw operator
   * field names read as tooling rather than a practice-facing record.
   */
  showConvertedData?: boolean;
  /**
   * Show the row's source key (the opaque import provenance id) as a subline.
   * Default on (admin — useful for debugging a mapping). A practice host hides
   * it (GDN-2349): the name + DOB · MRN already identify the record, so the raw
   * `patient:<uuid>` key is noise there.
   */
  showSourceKey?: boolean;
  /**
   * Per-field conflict resolution (GDN-2302). When provided, a matched conflict
   * row renders each competing field as a current/imported picker (defaulting
   * to imported) with a single Save, instead of the read-only diff + whole-
   * record keep/take buttons. On Save the host receives the per-field picks and
   * maps them to the resolve action (all imported → take_import, all current →
   * keep_existing, mixed → survivorship). Import-as-new / skip stay as secondary
   * buttons. Omit (admin) to keep the diff + whole-record decision buttons.
   */
  fieldResolution?: {
    onResolve: (
      rowId: string,
      picks: Record<string, 'existing' | 'incoming'>,
    ) => void;
  };
  /**
   * Use plain, practice-facing copy for a conflict row that has no field-level
   * diff (GDN-2302): "didn't match anyone — add or skip" when it also matched
   * nothing, or "couldn't identify the differing fields" when it matched. Off by
   * default (admin), which keeps the operator-facing blurb plus the "decide from
   * the converted data below, or send it to the practice admin" guidance — copy
   * that only makes sense while the converted-data dump is shown.
   */
  plainEmptyConflictCopy?: boolean;
  /** Rendered inside the collapsible Provenance disclosure. */
  provenance?: ReactNode;
}

// ---------------------------------------------------------------------------
// Verdict metadata — badge label, palette, icon. Single source for the chips.
// ---------------------------------------------------------------------------

/**
 * The imported record's own identifying line (GDN-2349): DOB · MRN pulled from
 * its data payload, shown on patient rows so the operator can compare the record
 * under review against the ambiguous-match candidate (which shows the same
 * fields). Only what's actually present — returns undefined when neither is set,
 * so a record we have no demographics for stays as just its name.
 */
function importedPatientIdentity(row: ReviewResourceRow): string | undefined {
  if (row.resourceType !== 'patient') return undefined;
  const d = row.data ?? {};
  const asStr = (v: unknown): string =>
    typeof v === 'string' && v.trim() ? v.trim() : '';
  const parts = [
    asStr(d.dob) && `DOB ${asStr(d.dob)}`,
    asStr(d.identifier) && `MRN ${asStr(d.identifier)}`,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : undefined;
}

type VerdictColor = 'info' | 'success' | 'primary' | 'warning' | 'error';

const VERDICT_META: Record<
  ImportVerdict,
  { label: string; color: VerdictColor; icon: ReactElement }
> = {
  new: {
    label: 'New',
    color: 'info',
    icon: <AddCircleOutlinedIcon fontSize="small" />,
  },
  unchanged: {
    label: 'Unchanged',
    color: 'success',
    icon: <CheckCircleOutlinedIcon fontSize="small" />,
  },
  update: {
    label: 'Update',
    color: 'primary',
    icon: <AutoAwesomeOutlinedIcon fontSize="small" />,
  },
  conflict: {
    label: 'Needs review',
    color: 'warning',
    icon: <WarningAmberOutlinedIcon fontSize="small" />,
  },
  ambiguous: {
    label: 'Needs match',
    color: 'warning',
    icon: <PersonSearchOutlinedIcon fontSize="small" />,
  },
  unprocessable: {
    label: "Couldn't read",
    color: 'error',
    icon: <ErrorOutlinedIcon fontSize="small" />,
  },
};

/** Plain-English explanation of each verdict, shown when a row is expanded. */
const VERDICT_BLURB: Record<ImportVerdict, string> = {
  new: 'Not on file — this will be created as a new record.',
  unchanged: 'Already on file and identical — nothing to do.',
  update:
    'Already on file. The import fills fields that were blank; nothing already on the record is changed.',
  conflict:
    'Already on file, but the import has a different value for a field that is already set — choose which to keep.',
  ambiguous:
    'More than one existing patient could be this record — pick the right one, or create a new patient.',
  unprocessable: "Couldn't be read from the source, so it's excluded from the import.",
};

/** Operator-facing explanation of WHY a row is ambiguous (GDN-2244). */
const MATCH_BASIS_BLURB: Record<string, string> = {
  identifier_collision:
    "The import's MRN matches an existing patient, but the name and date of " +
    'birth do not — most likely two different people whose systems reuse the ' +
    'same MRN. Link only if you are sure they are the same person.',
  weak_demographic_match:
    'A similar existing patient was found, but the details do not fully ' +
    'agree — confirm before linking.',
  ambiguous_identifier:
    'More than one existing patient carries this MRN — pick the right one.',
  ambiguous_match:
    'More than one existing patient looks like this record — pick the right one.',
};

/**
 * Honest singular/plural prompt for an ambiguous row (GDN-2344). A single
 * candidate is one possible match to confirm, not "more than one" to pick
 * between — the old copy contradicted a one-candidate picker.
 */
const ambiguousBlurb = (candidateCount: number): string =>
  candidateCount <= 1
    ? 'This import may be an existing patient — confirm it’s the same person, or create a new patient.'
    : 'More than one existing patient could be this record — pick the right one, or create a new patient.';

const VERDICT_ORDER: ImportVerdict[] = [
  'conflict',
  'ambiguous',
  'update',
  'new',
  'unchanged',
  'unprocessable',
];

type VerdictFilter = ImportVerdict | 'all';

function isConflict(row: ReviewResourceRow): boolean {
  return row.verdict === 'conflict';
}

function isAmbiguous(row: ReviewResourceRow): boolean {
  return row.verdict === 'ambiguous';
}

const ROW_GAP = 1;

// Auto-expand every needs-decision row only up to this many; past it the open
// diffs bury the list (org 3 arrived with 535).
const AUTO_EXPAND_MAX = 25;

// Ref-target chip treatment (GDN-2376): a short state word + soft color per
// resolved state, so "encounter: pending" reads at a glance. committed →
// success (and links out), staged → warning (drills in), missing → error.
const REF_STATE_META: Record<
  RefTarget['state'],
  { word: string; color: 'success' | 'warning' | 'error' }
> = {
  committed: { word: 'committed', color: 'success' },
  staged: { word: 'pending', color: 'warning' },
  missing: { word: 'missing', color: 'error' },
};

// ---------------------------------------------------------------------------
// Field diff — the existing → incoming pair, shared by competing + auto-merge.
// ---------------------------------------------------------------------------

/**
 * Structured values (a patient's primary address arrives as a JSON object)
 * read as raw JSON in diffs. Flatten one level to "street, city, state" so
 * the operator compares values, not syntax. Non-JSON strings pass through.
 */
export function formatFieldValue(value: string): string {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    const flatten = (v: unknown): string => {
      if (v === null || v === undefined || v === '') return '';
      if (Array.isArray(v)) return v.map(flatten).filter(Boolean).join('; ');
      if (typeof v === 'object')
        return Object.values(v as Record<string, unknown>)
          .map(flatten)
          .filter(Boolean)
          .join(', ');
      return String(v);
    };
    return flatten(parsed) || value;
  } catch {
    return value;
  }
}

/**
 * One-line descriptor for an appointment row: "{type} · {when} · {duration}",
 * e.g. "Psychiatric Intake · Jun 24, 2026 2:00 PM · 60 min". An appointment's
 * raw source key (appointment:<uuid>) tells the operator nothing; the patient
 * leads the row (GDN-2269) and this carries the appointment's own detail.
 * Missing/unparseable parts are dropped rather than shown blank.
 */
export function appointmentDescriptor(
  data: Record<string, unknown> | undefined,
): string {
  const d = data ?? {};
  const str = (k: string): string =>
    typeof d[k] === 'string' ? (d[k] as string) : '';
  const parts: string[] = [];
  const type = (str('encounter_type') || str('title')).replaceAll('_', ' ').trim();
  if (type) parts.push(type);
  const raw = str('start_time');
  if (raw) {
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      parts.push(format(parsed, 'MMM d, yyyy h:mm a'));
    }
  }
  const duration = d.duration;
  if (typeof duration === 'number' && duration > 0) parts.push(`${duration} min`);
  return parts.join(' · ');
}

function FieldDiffRow({
  diff,
  mode,
}: {
  diff: ReviewFieldDiff;
  mode: 'competing' | 'fill';
}) {
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}
    >
      <Typography
        variant="caption"
        sx={{ minWidth: 120, color: 'text.secondary' }}
      >
        {diff.field}
      </Typography>
      {mode === 'competing' ? (
        <>
          <Typography
            variant="body2"
            sx={{ textDecoration: 'line-through', color: 'text.disabled' }}
          >
            {formatFieldValue(diff.existing) || '—'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            →
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatFieldValue(diff.incoming) || '—'}
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatFieldValue(diff.incoming) || '—'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            (field was blank)
          </Typography>
        </>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Inspect — flat key/value view of the converted payload.
// ---------------------------------------------------------------------------

/**
 * Render a nested object/array from the converted payload as a readable
 * "key: value · key: value" line instead of raw JSON — a patient's primary
 * address, an appointment's metadata. Field labels are kept (underscores
 * spaced) so this stays a faithful convert-confidence check; empty members are
 * dropped and primitives pass through unchanged.
 */
export function humanizeStructured(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v))
    return v.map(humanizeStructured).filter(Boolean).join('; ');
  if (typeof v === 'object')
    // Filter on the humanized value, not the raw one: a member that is itself
    // an empty object/array (or all-null) humanizes to '' and must drop its
    // label too, else it leaves a dangling "key: " — same as the array branch.
    return Object.entries(v as Record<string, unknown>)
      .map(([k, val]) => [k.replace(/_/g, ' '), humanizeStructured(val)] as const)
      .filter(([, val]) => val !== '')
      .map(([k, val]) => `${k}: ${val}`)
      .join(' · ');
  return String(v);
}

function InspectData({ data }: { data: Record<string, unknown> }) {
  // Keep falsy-but-meaningful values (0, false, ''); only drop null/undefined.
  // This is the PHI-inspection view — a hidden 0-vs-null can matter clinically.
  const entries = Object.entries(data).filter(([, v]) => v != null);
  if (entries.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No fields on this record.
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        columnGap: 2,
        rowGap: 0.5,
      }}
    >
      {entries.map(([k, v]) => (
        <Box key={k} sx={{ display: 'contents' }}>
          <Typography variant="caption" color="text.secondary">
            {k}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
          >
            {/* Nested objects (address, metadata) read as "key: value", not
                raw JSON; a value that flattens to nothing falls back to JSON. */}
            {typeof v === 'object'
              ? humanizeStructured(v) || JSON.stringify(v)
              : String(v)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// One resource row
// ---------------------------------------------------------------------------

// GDN-2300: `escalate` ("Send to admin") is gone from the conflict vocabulary.
// Org-level review is no longer a staged commit-time decision — it's an
// orthogonal, persisted, toggleable flag (see the `orgReview` prop and the
// per-row "Flag for org review" affordance below), mirroring the GDN-2297
// selection dimension.
const DECISIONS: { value: ReviewDecision; label: string }[] = [
  { value: 'keep_existing', label: 'Keep existing' },
  { value: 'take_import', label: 'Take import' },
  // The match is wrong — these are different records; import as a new one
  // instead of writing onto the matched record.
  { value: 'import_as_new', label: 'Import as new' },
  { value: 'skip', label: 'Skip' },
];

// Whole-record decisions the per-field picker subsumes (GDN-2302): keeping or
// taking every field IS keep_existing / take_import, so in field-resolution
// mode these don't render as their own buttons — only import_as_new / skip do.
const FIELD_PICKER_SUBSUMES: ReadonlySet<ReviewDecision> = new Set<ReviewDecision>([
  'keep_existing',
  'take_import',
]);

// Friendly labels for the operator field keys shown to a practice admin
// (GDN-2302). Falls back to de-snaking the raw key.
const FIELD_LABELS: Record<string, string> = {
  dob: 'date of birth',
  ssn: 'SSN',
  mrn: 'MRN',
  npi: 'NPI',
  dea: 'DEA',
  mobile_phone: 'mobile phone',
  home_phone: 'home phone',
  address_line1: 'address',
  address_line2: 'address line 2',
  zip: 'ZIP',
};
function humanizeField(field: string): string {
  return FIELD_LABELS[field] ?? field.replace(/_/g, ' ');
}

const FIELD_TOGGLE_SX = {
  textTransform: 'none',
  flexDirection: 'column',
  lineHeight: 1.2,
  px: 1.5,
  py: 0.5,
} as const;

/**
 * Per-field conflict resolution (GDN-2302): each competing field is a
 * current/imported choice (defaulting to imported), committed with one Save.
 * The control only collects the choices — the host maps a uniform set to
 * keep_existing/take_import and a mix to survivorship.
 */
function FieldResolutionPicker({
  competing,
  disabled,
  onSave,
}: {
  competing: ReviewFieldDiff[];
  disabled?: boolean;
  onSave: (picks: Record<string, 'existing' | 'incoming'>) => void;
}) {
  const [picks, setPicks] = useState<Record<string, 'existing' | 'incoming'>>(
    () => Object.fromEntries(competing.map((f) => [f.field, 'incoming' as const])),
  );
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
      {competing.map((f) => (
        <Box
          key={f.field}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}
        >
          <Typography
            variant="body2"
            sx={{
              minWidth: 140,
              color: 'text.secondary',
              textTransform: 'capitalize',
            }}
          >
            {humanizeField(f.field)}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={picks[f.field]}
            disabled={disabled}
            onChange={(_, v: 'existing' | 'incoming' | null) =>
              v && setPicks((prev) => ({ ...prev, [f.field]: v }))
            }
          >
            <ToggleButton value="existing" sx={FIELD_TOGGLE_SX}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {formatFieldValue(f.existing) || '—'}
              </Box>
              <Typography variant="caption" color="text.secondary">
                current
              </Typography>
            </ToggleButton>
            <ToggleButton value="incoming" sx={FIELD_TOGGLE_SX}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {formatFieldValue(f.incoming) || '—'}
              </Box>
              <Typography variant="caption" color="text.secondary">
                imported
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      ))}
      <Box>
        <Button
          variant="contained"
          size="small"
          disabled={disabled}
          onClick={() => onSave(picks)}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
}

function ResourceRow({
  row,
  expanded,
  onToggle,
  decision,
  onDecision,
  decisionOptions,
  conflictHeader,
  conflictBlurb,
  decisionsDisabled,
  matchResolution,
  onMatchResolution,
  onCommitPatient,
  onCommitRow,
  commitBlockedReason,
  commitInFlight,
  onViewDocument,
  resourceHref,
  onOpenStagedRef,
  selected,
  onToggleSelect,
  orgReviewNeeded,
  orgReviewBy,
  onToggleOrgReview,
  showStatusChip = true,
  patientHref,
  candidateHref,
  showConvertedData = true,
  showSourceKey = true,
  onResolveFields,
  plainEmptyConflict = false,
}: {
  row: ReviewResourceRow;
  expanded: boolean;
  onToggle: () => void;
  decision: ReviewDecision | null;
  onDecision: (d: ReviewDecision | null) => void;
  decisionOptions: { value: ReviewDecision; label: string }[];
  /** Override for the conflict section header (default admin wording). */
  conflictHeader?: string;
  /** Override for the conflict blurb on an expanded conflict row. */
  conflictBlurb?: string;
  /** Disable this row's decision buttons (e.g. its resolve is in flight). */
  decisionsDisabled?: boolean;
  matchResolution: ReviewMatchResolution | null;
  onMatchResolution: (r: ReviewMatchResolution | null) => void;
  /** Present on decided/decision-free patient rows when the host commits. */
  onCommitPatient?: () => void;
  /**
   * Dependency-aware per-row commit (GDN-2376), pre-bound to this row. Rendered
   * only for NON-patient rows (patients keep `onCommitPatient`), so a row never
   * shows two Commit buttons.
   */
  onCommitRow?: () => void;
  /** Host-resolved up-front blocker for the per-row commit; disables it (GDN-2376). */
  commitBlockedReason?: string;
  commitInFlight?: boolean;
  onViewDocument?: () => void;
  /** Resolve a committed ref target to its Resource Explorer URL (GDN-2376). */
  resourceHref?: (
    resourceType: string,
    committedResourceId: string,
  ) => string | undefined;
  /** Drill into a staged ref target's review row (GDN-2376). */
  onOpenStagedRef?: (target: RefTarget) => void;
  /** Selection (GDN-2297): checked state. Omit `onToggleSelect` to hide it. */
  selected?: boolean;
  onToggleSelect?: (next: boolean) => void;
  /**
   * Org-review flag (GDN-2300): current flagged state + provenance ('agent' or
   * a user id). Omit `onToggleOrgReview` to hide the affordance entirely.
   */
  orgReviewNeeded?: boolean;
  orgReviewBy?: string | null;
  onToggleOrgReview?: (needed: boolean) => void;
  /** Show the verdict/status chip (GDN-2302). Default on. */
  showStatusChip?: boolean;
  /** When set, the matched record links here (GDN-2302). */
  patientHref?: string;
  /** Resolve an ambiguous-match candidate's patient id to its page URL (GDN-2344). */
  candidateHref?: (patientId: string) => string | undefined;
  /** Show the raw converted-data dump (GDN-2302). Default on. */
  showConvertedData?: boolean;
  /** Show the source-key subline (GDN-2349). Default on; practice hides it. */
  showSourceKey?: boolean;
  /**
   * Per-field resolution (GDN-2302): when set, a matched conflict row renders
   * the current/imported field picker instead of the diff + keep/take buttons,
   * and Save calls this with the picks. Omit to keep the whole-record buttons.
   */
  onResolveFields?: (picks: Record<string, 'existing' | 'incoming'>) => void;
  /** Plain practice copy for an empty-diff conflict row (GDN-2302). Default off. */
  plainEmptyConflict?: boolean;
}) {
  const meta = VERDICT_META[row.verdict];
  const importedIdentity = importedPatientIdentity(row);
  const conflict = isConflict(row);
  const ambiguous = isAmbiguous(row);
  const resolved =
    (conflict && decision != null) || (ambiguous && matchResolution != null);
  // Per-row decision buttons: if the host constrains which decisions are valid
  // for this row (`allowedDecisions`), render only those; otherwise render the
  // full set (admin host — unchanged).
  const allowedDecisions = row.allowedDecisions;
  const visibleDecisionOptions = allowedDecisions
    ? decisionOptions.filter((d) => allowedDecisions.includes(d.value))
    : decisionOptions;
  // Field-picker mode (GDN-2302) shows the current/imported picker for a matched
  // conflict (row has competing fields). keep/take are subsumed by the picker,
  // so only the non-merge options (import as new / skip) stay as buttons.
  const useFieldPicker =
    !!onResolveFields && !!row.competing && row.competing.length > 0;
  const secondaryDecisionOptions = visibleDecisionOptions.filter(
    (d) => !FIELD_PICKER_SUBSUMES.has(d.value),
  );
  // A conflict row with no competing fields isn't a "choose which to keep"
  // decision (GDN-2302). If it also matched nothing, it's an unmatched record —
  // the only choice is add-as-new vs skip.
  const noCompeting = conflict && (row.competing?.length ?? 0) === 0;
  const unmatched = noCompeting && !row.matchedSummary;
  // Ambiguous blurb is count-aware (GDN-2344): a lone candidate is one lead, not
  // "more than one", so never show the multi-match copy for a single candidate —
  // even a match-basis blurb, whose wording assumes multiple. Genuine multi-match
  // rows keep the match-basis explanation.
  const candidateCount = row.matchCandidates?.length ?? 0;
  const baseBlurb = ambiguous
    ? candidateCount <= 1
      ? ambiguousBlurb(candidateCount)
      : (row.matchBasis && MATCH_BASIS_BLURB[row.matchBasis]) ||
        ambiguousBlurb(candidateCount)
    : conflict && conflictBlurb
      ? conflictBlurb
      : VERDICT_BLURB[row.verdict];
  // Only a plain-copy (practice) host restates an empty-diff conflict row in
  // practice-facing language — say what it actually is instead of the conflict
  // scaffolding. Admin keeps `baseBlurb` plus the converted-data guidance below.
  const expandedBlurb =
    plainEmptyConflict && unmatched
      ? "This record didn't match anyone already on file — add it as a new record, or skip it."
      : plainEmptyConflict && noCompeting
        ? 'We couldn’t identify the differing fields — choose an option below.'
        : baseBlurb;

  const decisionButtons = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {visibleDecisionOptions.map((d) => (
        <Button
          key={d.value}
          size="small"
          variant={decision === d.value ? 'contained' : 'outlined'}
          color="primary"
          disabled={decisionsDisabled}
          onClick={() => onDecision(decision === d.value ? null : d.value)}
        >
          {d.label}
        </Button>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: (t) =>
          (conflict || ambiguous) && !resolved
            ? alpha(t.palette.warning.main, 0.04)
            : 'transparent',
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: ROW_GAP,
          px: 1,
          py: 1,
          cursor: 'pointer',
        }}
      >
        {onToggleSelect && (
          <Checkbox
            size="small"
            checked={!!selected}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onToggleSelect(e.target.checked)}
            sx={{ p: 0.5, mr: -0.5 }}
            slotProps={{ input: { 'aria-label': `Select ${row.label}` } }}
          />
        )}
        <Chip
          label={row.resourceType}
          size="small"
          variant="outlined"
          sx={{ minWidth: 96, textTransform: 'capitalize' }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {row.resourceType === 'appointment' && row.patientLabel ? (
            // Appointments lead with the patient (whose chart it lands on is the
            // identity); the appointment's own type/date/duration is the detail
            // line. The raw source key is dropped from the row (GDN-2269).
            <>
              {row.patientUnlinked ? (
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'warning.main',
                  }}
                >
                  <WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />
                  {row.patientLabel} — unlinked
                </Typography>
              ) : (
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                  {row.patientLabel}
                </Typography>
              )}
              {row.sublabel && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {row.sublabel}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                {row.label}
              </Typography>
              {importedIdentity && (
                // caption renders as an inline <span>; force block so it doesn't
                // run into the source-key line beside it (GDN-2349).
                <Typography
                  variant="caption"
                  color="text.primary"
                  noWrap
                  sx={{ fontWeight: 500, display: 'block' }}
                >
                  {importedIdentity}
                </Typography>
              )}
              {showSourceKey && row.sublabel && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ display: 'block' }}
                >
                  {row.sublabel}
                </Typography>
              )}
              {row.patientLabel &&
                (row.patientUnlinked ? (
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'warning.main',
                    }}
                  >
                    <WarningAmberOutlinedIcon sx={{ fontSize: 14 }} />
                    Unlinked — {row.patientLabel}
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ color: 'text.secondary' }}
                  >
                    Patient: {row.patientLabel}
                  </Typography>
                ))}
            </>
          )}

          {/* Dependency ref chips (GDN-2376): one soft chip per ref target,
              color-coded by resolved state. Committed → links to the live
              record; staged → drills into that row's review; missing → static.
              stopPropagation keeps a chip click off the row expand/collapse. */}
          {row.refTargets && row.refTargets.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {row.refTargets.map((t, i) => {
                const stateMeta = REF_STATE_META[t.state];
                const chip = (
                  <UiChip
                    label={`${t.role}: ${t.label || t.source_key} · ${stateMeta.word}`}
                    size="xSmall"
                    variant="soft"
                    color={stateMeta.color}
                    onClick={
                      t.state === 'staged' && onOpenStagedRef
                        ? (e) => {
                            e.stopPropagation();
                            onOpenStagedRef(t);
                          }
                        : undefined
                    }
                    sx={{ maxWidth: '100%' }}
                  />
                );
                const href =
                  t.state === 'committed' && t.committed_resource_id
                    ? resourceHref?.(t.resource_type, t.committed_resource_id)
                    : undefined;
                const key = `${t.role}:${t.source_key}:${i}`;
                return href ? (
                  <Link
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noopener"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ display: 'inline-flex', maxWidth: '100%' }}
                  >
                    {chip}
                  </Link>
                ) : (
                  <Box
                    key={key}
                    component="span"
                    sx={{ display: 'inline-flex', maxWidth: '100%' }}
                  >
                    {chip}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {row.committed && (
          <Chip
            icon={<CheckCircleOutlinedIcon fontSize="small" />}
            label="Committed"
            size="small"
            color="success"
            variant="filled"
          />
        )}
        {showStatusChip && (
          <Chip
            icon={resolved ? <CheckCircleOutlinedIcon fontSize="small" /> : meta.icon}
            label={resolved ? 'Resolved' : meta.label}
            size="small"
            color={resolved ? 'success' : meta.color}
            variant="outlined"
          />
        )}

        {/* Org-review flag (GDN-2300): an immediate persisted toggle, not a
            staged decision. Flagged rows read out their provenance; the button
            stops propagation so it doesn't expand/collapse the row. */}
        {onToggleOrgReview &&
          (orgReviewNeeded ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip
                icon={<OutlinedFlagIcon fontSize="small" />}
                label="Org review"
                size="small"
                color="warning"
                variant="filled"
              />
              {orgReviewBy && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  by {orgReviewBy === 'agent' ? 'agent' : 'you'}
                </Typography>
              )}
              <Button
                size="small"
                color="inherit"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleOrgReview(false);
                }}
              >
                Unflag
              </Button>
            </Box>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<OutlinedFlagIcon fontSize="small" />}
              onClick={(e) => {
                e.stopPropagation();
                onToggleOrgReview(true);
              }}
            >
              Flag for org review
            </Button>
          ))}

        {onViewDocument && (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onViewDocument();
            }}
          >
            View document
          </Button>
        )}

        {onCommitPatient && (
          <Tooltip title="Commit this patient record now — children commit later by type">
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={commitInFlight}
                onClick={(e) => {
                  e.stopPropagation();
                  onCommitPatient();
                }}
              >
                Commit
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Dependency-aware per-row commit (GDN-2376) for any NON-patient type —
            patients keep their own `onCommitPatient` button above, so a row
            never shows two. Only for still-staged rows; the BE commits it if its
            deps are met, else blocks it with a clear ref reason.
            `commitBlockedReason` (host-resolved, e.g. an unmapped required
            provider) names an up-front blocker the ref chips don't cover, so the
            button disables and says why instead of failing on click. */}
        {onCommitRow &&
          row.resourceType !== 'patient' &&
          !row.committed &&
          row.verdict !== 'unprocessable' && (
            <Tooltip
              title={
                commitBlockedReason ||
                'Commit this record now if its dependencies are met'
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={commitInFlight || !!commitBlockedReason}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCommitRow();
                  }}
                >
                  Commit
                </Button>
              </span>
            </Tooltip>
          )}

        <IconButton size="small" aria-label={expanded ? 'Collapse' : 'Expand'}>
          {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 6, pb: 2, pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {expandedBlurb}
          </Typography>

          {row.matchedSummary && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1.5 }}
            >
              Matched existing record:{' '}
              {patientHref ? (
                // Opens in a new tab so the reviewer can check the existing
                // chart without losing their place in the queue (GDN-2302).
                <Link
                  href={patientHref}
                  target="_blank"
                  rel="noopener"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                  }}
                >
                  {row.matchedSummary}
                  <OpenInNewIcon sx={{ fontSize: 13 }} />
                </Link>
              ) : (
                <strong>{row.matchedSummary}</strong>
              )}
            </Typography>
          )}

          {conflict && (
            <Box sx={{ mb: 1.5 }}>
              {row.competing && row.competing.length > 0 ? (
                <>
                  <Typography
                    variant="overline"
                    color="warning.main"
                    sx={{ fontWeight: 700, display: 'block' }}
                  >
                    {conflictHeader ?? 'Conflicting values — choose which to keep'}
                  </Typography>

                  {useFieldPicker ? (
                    // Per-field resolution (GDN-2302): pick current/imported per
                    // field, Save once. import-as-new / skip stay as secondary
                    // text actions beside it.
                    <Box sx={{ mt: 0.5 }}>
                      <FieldResolutionPicker
                        competing={row.competing}
                        disabled={decisionsDisabled}
                        onSave={onResolveFields!}
                      />
                      {secondaryDecisionOptions.length > 0 && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            or
                          </Typography>
                          {secondaryDecisionOptions.map((d) => (
                            <Button
                              key={d.value}
                              size="small"
                              variant="text"
                              color="inherit"
                              disabled={decisionsDisabled}
                              onClick={() =>
                                onDecision(decision === d.value ? null : d.value)
                              }
                            >
                              {d.label}
                            </Button>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          mt: 0.5,
                          mb: 1,
                        }}
                      >
                        {row.competing.map((d) => (
                          <FieldDiffRow key={d.field} diff={d} mode="competing" />
                        ))}
                      </Box>
                      {decisionButtons}
                    </>
                  )}
                </>
              ) : plainEmptyConflict ? (
                // No competing fields, plain-copy host: the expanded blurb
                // already says what this row is (unmatched / no diff), so skip
                // the conflict header and the "no differences" line — just offer
                // the actions.
                decisionButtons
              ) : (
                // Admin (unchanged): field detail unavailable for this conflict —
                // point the operator at the converted-data dump below.
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5, mb: 1 }}
                  >
                    Field detail is unavailable for this conflict — decide from
                    the converted data below, or send it to the practice admin.
                  </Typography>
                  {decisionButtons}
                </>
              )}
            </Box>
          )}

          {/* Not gated on candidates being present: "create new" must stay
              reachable even if a row somehow arrives with an empty set. */}
          {ambiguous && (
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="overline"
                color="warning.main"
                sx={{ fontWeight: 700, display: 'block' }}
              >
                Which existing patient is this?
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  mt: 0.75,
                }}
              >
                {(row.matchCandidates ?? []).map((c) => {
                  const selected =
                    matchResolution?.action === 'link' &&
                    matchResolution.targetPatientId === c.patientId;
                  // Evidence line — the identifying detail we can show. No
                  // confidence %: the operator is answering "is this the same
                  // person?", not weighing a score (GDN-2344).
                  const evidence = [
                    c.dob ? `DOB ${c.dob}` : null,
                    c.identifier ? `MRN ${c.identifier}` : null,
                    c.reason === 'identifier_only' ? 'MRN only' : null,
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  const href = candidateHref?.(c.patientId);
                  return (
                    <Box
                      key={c.patientId}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <Button
                        size="small"
                        variant={selected ? 'contained' : 'outlined'}
                        disabled={decisionsDisabled}
                        onClick={() =>
                          onMatchResolution(
                            selected
                              ? null
                              : { action: 'link', targetPatientId: c.patientId },
                          )
                        }
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          textTransform: 'none',
                          py: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, lineHeight: 1.25 }}
                          >
                            {candidateDisplayName(c)}
                          </Typography>
                          {evidence && (
                            <Typography
                              variant="caption"
                              color={selected ? 'inherit' : 'text.secondary'}
                              sx={{ lineHeight: 1.25 }}
                            >
                              {evidence}
                            </Typography>
                          )}
                        </Box>
                      </Button>
                      {href && (
                        // Opens in a new tab so the reviewer can check the
                        // existing chart without losing their place (GDN-2344).
                        <Link
                          href={href}
                          target="_blank"
                          rel="noopener"
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.25,
                            whiteSpace: 'nowrap',
                            fontSize: 13,
                          }}
                        >
                          View patient
                          <OpenInNewIcon sx={{ fontSize: 13 }} />
                        </Link>
                      )}
                    </Box>
                  );
                })}
                <Button
                  size="small"
                  color="warning"
                  variant={
                    matchResolution?.action === 'create'
                      ? 'contained'
                      : 'outlined'
                  }
                  disabled={decisionsDisabled}
                  onClick={() =>
                    onMatchResolution(
                      matchResolution?.action === 'create'
                        ? null
                        : { action: 'create' },
                    )
                  }
                  sx={{ alignSelf: 'flex-start', textTransform: 'none', mt: 0.25 }}
                >
                  None of these — create new patient
                </Button>
              </Box>
            </Box>
          )}

          {row.autoMerge && row.autoMerge.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                {conflict
                  ? 'Also filling blank fields (automatic)'
                  : 'Filling blank fields — applied automatically, nothing overwritten'}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  mt: 0.5,
                }}
              >
                {row.autoMerge.map((d) => (
                  <FieldDiffRow key={d.field} diff={d} mode="fill" />
                ))}
              </Box>
            </Box>
          )}

          {showConvertedData && row.data && (
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                Converted data
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <InspectData data={row.data} />
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Browser
// ---------------------------------------------------------------------------

export function ImportReviewBrowser({
  sourceLabel,
  totalRecords,
  totals,
  rows,
  decisions: decisionsProp,
  onDecisionChange,
  decisionOptions = DECISIONS,
  copyOverrides,
  disabledRowIds,
  matchResolutions: matchResolutionsProp,
  onMatchResolutionChange,
  commit,
  onViewDocument,
  serverBrowse,
  selection,
  orgReview,
  onCommit,
  showCommitBar = true,
  showBulkActions = true,
  showStatusChips = true,
  showSummary = true,
  patientHref,
  candidateHref,
  resourceHref,
  onOpenStagedRef,
  onCommitRow,
  commitRowDisabledReason,
  showConvertedData = true,
  showSourceKey = true,
  fieldResolution,
  plainEmptyConflictCopy = false,
  provenance,
}: ImportReviewBrowserProps) {
  const [internalDecisions, setInternalDecisions] = useState<
    Record<string, ReviewDecision>
  >({});
  const decisions = decisionsProp ?? internalDecisions;
  const [internalMatchResolutions, setInternalMatchResolutions] = useState<
    Record<string, ReviewMatchResolution>
  >({});
  const matchResolutions = matchResolutionsProp ?? internalMatchResolutions;

  // Copy overrides (GDN-2271): every field falls back to the admin default, so
  // the admin host renders identically when `copyOverrides` is omitted.
  const copy = copyOverrides ?? {};
  const resolveBulkLabel = (
    label: string | ((n: number) => string) | undefined,
    fallback: (n: number) => string,
    count: number,
  ): string =>
    label === undefined
      ? fallback(count)
      : typeof label === 'function'
        ? label(count)
        : label;

  const [localTypeFilter, setLocalTypeFilter] = useState<string>('all');
  const [localVerdictFilter, setLocalVerdictFilter] =
    useState<VerdictFilter>('all');
  const [localSearch, setLocalSearch] = useState('');
  const typeFilter = serverBrowse ? serverBrowse.typeFilter : localTypeFilter;
  const setTypeFilter = serverBrowse
    ? serverBrowse.onTypeFilterChange
    : setLocalTypeFilter;
  const search = serverBrowse ? serverBrowse.search : localSearch;
  const setSearch = serverBrowse ? serverBrowse.onSearchChange : setLocalSearch;
  // Verdict is server-driven when the host wires it (GDN-2287): selecting a
  // chip re-fetches that verdict's page BE-side (filtered pre-pagination), so
  // the chip covers the whole import. Otherwise it slices the loaded rows
  // client-side, as before.
  const serverVerdict = !!serverBrowse?.onVerdictFilterChange;
  const verdictFilter: VerdictFilter = serverVerdict
    ? ((serverBrowse!.verdictFilter as VerdictFilter | undefined) ?? 'all')
    : localVerdictFilter;
  const setVerdictFilter = (v: VerdictFilter) => {
    if (serverVerdict) serverBrowse!.onVerdictFilterChange!(v);
    else setLocalVerdictFilter(v);
  };
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Rows needing a decision start expanded — conflicts and ambiguous matches
  // shouldn't be hidden. rows load async (empty on the first render), so a
  // lazy initializer would miss them; auto-expand once, when rows first
  // arrive. Only for small sets: auto-expanding a 500-decision import renders
  // a wall of open diffs (and a very heavy DOM) that buries everything else —
  // past the cap, rows open on click like any list.
  const autoExpandedRef = useRef(false);
  useEffect(() => {
    if (autoExpandedRef.current || rows.length === 0) return;
    autoExpandedRef.current = true;
    const needsDecision = rows.filter((r) => isConflict(r) || isAmbiguous(r));
    if (needsDecision.length > AUTO_EXPAND_MAX) return;
    setExpanded(new Set(needsDecision.map((r) => r.id)));
  }, [rows]);
  const [provenanceOpen, setProvenanceOpen] = useState(false);

  const rowResourceTypes = useMemo(
    () => Array.from(new Set(rows.map((r) => r.resourceType))).sort(),
    [rows],
  );
  const resourceTypes = serverBrowse?.resourceTypes ?? rowResourceTypes;
  const openConflicts = rows.filter(
    (r) => isConflict(r) && decisions[r.id] == null,
  ).length;
  const openMatches = rows.filter(
    (r) => isAmbiguous(r) && matchResolutions[r.id] == null,
  ).length;

  const setDecision = (id: string, d: ReviewDecision | null) => {
    if (!decisionsProp) {
      setInternalDecisions((prev) => {
        if (d == null) {
          const { [id]: _removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [id]: d };
      });
    }
    onDecisionChange?.(id, d);
  };

  const setMatchResolution = (id: string, r: ReviewMatchResolution | null) => {
    if (!matchResolutionsProp) {
      setInternalMatchResolutions((prev) => {
        if (r == null) {
          const { [id]: _removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [id]: r };
      });
    }
    onMatchResolutionChange?.(id, r);
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      // In server mode the server already applied type + search to its page —
      // re-applying the search to those rows would drop ones the server
      // matched by source key alone. The host's pinned out-of-page rows never
      // went through the server, so the filters apply to them client-side;
      // hiding them is safe because commit gating counts undecided work from
      // the full feeds, not from what's visible (GDN-2247).
      if (!serverBrowse || r.pinned) {
        if (typeFilter !== 'all' && r.resourceType !== typeFilter) return false;
        if (
          q &&
          !`${r.label} ${r.sublabel ?? ''} ${r.patientLabel ?? ''}`
            .toLowerCase()
            .includes(q)
        )
          return false;
      }
      // Verdict is applied BE-side when server-driven (GDN-2287) — the page is
      // already the selected verdict's rows, so don't re-slice it. Client slice
      // only in the legacy/local mode.
      if (!serverVerdict && verdictFilter !== 'all' && r.verdict !== verdictFilter)
        return false;
      return true;
    });
    // Needs-attention first (open conflicts / unmatched patients), then by
    // verdict.
    const needsAttention = (r: ReviewResourceRow) =>
      (isConflict(r) && decisions[r.id] == null) ||
      (isAmbiguous(r) && matchResolutions[r.id] == null);
    return filtered.sort((a, b) => {
      const aAttn = needsAttention(a);
      const bAttn = needsAttention(b);
      if (aAttn !== bAttn) return aAttn ? -1 : 1;
      return VERDICT_ORDER.indexOf(a.verdict) - VERDICT_ORDER.indexOf(b.verdict);
    });
  }, [
    rows,
    typeFilter,
    verdictFilter,
    serverVerdict,
    search,
    decisions,
    matchResolutions,
    serverBrowse,
  ]);

  // Bulk actions target what the operator is looking at — the filtered set —
  // so "keep existing for N conflicts" always matches the rows on screen.
  const visibleOpenConflicts = visible.filter(
    (r) => isConflict(r) && decisions[r.id] == null,
  );
  const visibleOpenMatches = visible.filter(
    (r) => isAmbiguous(r) && matchResolutions[r.id] == null,
  );

  // A row can commit individually once nothing about it awaits a decision.
  const rowCommittable = (r: ReviewResourceRow) =>
    (!isConflict(r) || decisions[r.id] != null) &&
    (!isAmbiguous(r) || matchResolutions[r.id] != null) &&
    r.verdict !== 'unprocessable';
  // Cohort commit: the current verdict filter, as complete patient keys from
  // the host (never just the loaded window). Only patient cohorts commit this
  // way — with a non-patient type filter active the button would lie.
  const cohortKeys =
    commit && verdictFilter !== 'all' && (typeFilter === 'all' || typeFilter === 'patient')
      ? commit.patientKeysByVerdict[verdictFilter]
      : undefined;

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const openItems = openConflicts + openMatches;
  const commitLabel = (() => {
    const parts: string[] = [];
    if (totals.update)
      parts.push(`${totals.update} update${totals.update > 1 ? 's' : ''}`);
    if (totals.new) parts.push(`${totals.new} new`);
    if (totals.unchanged) parts.push(`${totals.unchanged} unchanged skipped`);
    const head = parts.length ? `Commit — ${parts.join(', ')}` : 'Commit';
    const open: string[] = [];
    if (openConflicts)
      open.push(`${openConflicts} conflict${openConflicts > 1 ? 's' : ''}`);
    if (openMatches)
      open.push(`${openMatches} match${openMatches > 1 ? 'es' : ''}`);
    return open.length ? `${head} · ${open.join(' + ')} open` : head;
  })();

  return (
    <Box>
      {/* Header — hidden when the host renders its own (showSummary=false),
          unless there's a provenance disclosure to surface. */}
      {(showSummary || provenance) && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        {showSummary && (
          <Box>
            <Typography variant="h6">{copy.summaryTitle ?? 'Import review'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {sourceLabel ? `${sourceLabel} · ` : ''}
              {copy.summaryCountLabel
                ? copy.summaryCountLabel(totalRecords)
                : `${totalRecords} resources · ${
                    totals.unprocessable
                      ? `${totals.unprocessable} couldn't be read`
                      : 'all converted'
                  }`}
            </Typography>
          </Box>
        )}
        {provenance && (
          <Button
            size="small"
            onClick={() => setProvenanceOpen((o) => !o)}
            endIcon={
              provenanceOpen ? (
                <KeyboardArrowDownIcon />
              ) : (
                <KeyboardArrowRightIcon />
              )
            }
          >
            Provenance
          </Button>
        )}
      </Box>
      )}
      {provenance && (
        <Collapse in={provenanceOpen} unmountOnExit>
          <Box sx={{ mb: 2 }}>{provenance}</Box>
        </Collapse>
      )}

      {/* Summary + verdict filter chips */}
      {showSummary && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <Chip
            label={`All ${totalRecords}`}
            size="small"
            variant={verdictFilter === 'all' ? 'filled' : 'outlined'}
            onClick={() => setVerdictFilter('all')}
          />
          {VERDICT_ORDER.filter((v) => totals[v] > 0).map((v) => (
            <Chip
              key={v}
              icon={VERDICT_META[v].icon}
              label={`${VERDICT_META[v].label} ${totals[v]}`}
              size="small"
              color={VERDICT_META[v].color}
              variant={verdictFilter === v ? 'filled' : 'outlined'}
              onClick={() => setVerdictFilter(v)}
            />
          ))}
        </Box>
      )}

      {/* Selection bar (GDN-2297): the persisted, shared working set both this
          UI and the MCP agent curate. Shown once anything is selected. */}
      {selection && selection.selectedTotal > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {selection.selectedTotal.toLocaleString()} selected
          </Typography>
          {selection.byActor && selection.byActor.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {selection.byActor
                .map((a) => `${a.count.toLocaleString()} by ${a.label}`)
                .join(' · ')}
            </Typography>
          )}
          {selection.onFilterChange && (
            <Chip
              label={`Selected ${selection.selectedTotal.toLocaleString()}`}
              size="small"
              color="secondary"
              variant={selection.filterActive ? 'filled' : 'outlined'}
              onClick={() =>
                selection.onFilterChange?.(!selection.filterActive)
              }
            />
          )}
          <Box sx={{ flex: 1 }} />
          <Button size="small" color="inherit" onClick={selection.onClear}>
            Clear all
          </Button>
        </Box>
      )}

      {/* Org-review bar (GDN-2300): the persisted flag both this UI and the MCP
          agent curate. Parallels the selection bar; no "Clear all" — org review
          is unflagged per row, not bulk-cleared. Visible whenever there's
          anything to act on: rows flagged (the read-out + Flagged segment) OR a
          filter control with a non-empty import behind it (GDN-2366 — "All" and
          "Not flagged" are meaningful before anything's been flagged, when
          "Not flagged" is the whole import). The "N flagged" read-out itself
          stays gated on total > 0 so it never renders a bare "0 flagged". */}
      {orgReview &&
        (orgReview.total > 0 ||
          (!!orgReview.onFilterModeChange &&
            (orgReview.notFlaggedTotal ?? 0) > 0)) && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
          }}
        >
          {orgReview.total > 0 && (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {orgReview.total.toLocaleString()} flagged for org review
              </Typography>
              {orgReview.byActor && orgReview.byActor.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {orgReview.byActor
                    .map((a) => `${a.count.toLocaleString()} by ${a.label}`)
                    .join(' · ')}
                </Typography>
              )}
            </>
          )}
          {orgReview.onFilterModeChange && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={orgReview.filterMode ?? 'all'}
              onChange={(_, mode) => {
                // ToggleButtonGroup passes null when the active button is
                // re-clicked; ignore it so there's always a selected segment
                // ('all' is the neutral one to fall back to).
                if (mode) orgReview.onFilterModeChange?.(mode);
              }}
              aria-label="Filter by org-review flag"
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="flagged">
                <OutlinedFlagIcon fontSize="small" sx={{ mr: 0.5 }} />
                Flagged {orgReview.total.toLocaleString()}
              </ToggleButton>
              <ToggleButton value="not_flagged">
                Not flagged {(orgReview.notFlaggedTotal ?? 0).toLocaleString()}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      )}

      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        {/* A single-type import (e.g. the all-patients practice inbox) has
            nothing to filter by type, so the dropdown is just chrome. */}
        {resourceTypes.length > 1 && (
          <TextField
            select
            size="small"
            label="Type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">All types</MenuItem>
            {resourceTypes.map((t) => (
              <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        )}
        {serverBrowse?.onStatusFilterChange && (
          <TextField
            select
            size="small"
            label="Status"
            value={serverBrowse.statusFilter ?? 'all'}
            onChange={(e) => serverBrowse.onStatusFilterChange?.(e.target.value)}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="committed">Committed</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </TextField>
        )}
        <TextField
          size="small"
          placeholder="Search records…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      {/* Bulk actions: one decision applied to every still-open VISIBLE item
          (the current filters scope them). Each is individually reversible
          afterwards, so no confirm dialog. A terminal host that resolves per
          row (the practice inbox) hides them (GDN-2302). */}
      {showBulkActions &&
        (visibleOpenConflicts.length > 0 || visibleOpenMatches.length > 0) && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            mb: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Bulk:
          </Typography>
          {visibleOpenConflicts.length > 0 && (
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  visibleOpenConflicts.forEach((r) =>
                    setDecision(r.id, 'keep_existing'),
                  )
                }
              >
                {resolveBulkLabel(
                  copy.bulkKeepLabel,
                  (n) => `Keep existing for ${n} conflict${n > 1 ? 's' : ''}`,
                  visibleOpenConflicts.length,
                )}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  visibleOpenConflicts.forEach((r) =>
                    setDecision(r.id, 'take_import'),
                  )
                }
              >
                {resolveBulkLabel(
                  copy.bulkTakeLabel,
                  (n) => `Take import for ${n} conflict${n > 1 ? 's' : ''}`,
                  visibleOpenConflicts.length,
                )}
              </Button>
            </>
          )}
          {visibleOpenMatches.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() =>
                visibleOpenMatches.forEach((r) =>
                  setMatchResolution(r.id, { action: 'create' }),
                )
              }
            >
              Create new patient for {visibleOpenMatches.length} unmatched
            </Button>
          )}
        </Box>
      )}

      {/* Cohort commit (GDN-2251): commit every patient with the filtered
          verdict — the complete set from the host, not just this page. The
          server re-checks decisions, so a premature click blocks cleanly. */}
      {commit && cohortKeys && cohortKeys.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Tooltip title="Commits these patient records now — their records (encounters, meds, …) commit later by type">
            <span>
              <Button
                size="small"
                variant="contained"
                disabled={commit.inFlight}
                onClick={() => commit.onCommitPatients(cohortKeys)}
              >
                Commit {cohortKeys.length.toLocaleString()}{' '}
                {VERDICT_META[verdictFilter as ImportVerdict].label.toLowerCase()}{' '}
                patient{cohortKeys.length > 1 ? 's' : ''}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* List */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        {visible.length === 0 ? (
          <Typography sx={{ p: 3, textAlign: 'center' }} color="text.secondary">
            No resources match this filter.
          </Typography>
        ) : (
          visible.map((row) => (
            <ResourceRow
              key={row.id}
              row={row}
              expanded={expanded.has(row.id)}
              onToggle={() => toggle(row.id)}
              decision={decisions[row.id] ?? null}
              onDecision={(d) => setDecision(row.id, d)}
              decisionOptions={decisionOptions}
              conflictHeader={copy.conflictHeader}
              conflictBlurb={copy.conflictBlurb}
              decisionsDisabled={disabledRowIds?.has(row.id)}
              matchResolution={matchResolutions[row.id] ?? null}
              onMatchResolution={(r) => setMatchResolution(row.id, r)}
              onCommitPatient={
                commit && row.patientKey && rowCommittable(row)
                  ? () => commit.onCommitPatients([row.patientKey as string])
                  : undefined
              }
              onCommitRow={
                onCommitRow && rowCommittable(row)
                  ? () => onCommitRow(row)
                  : undefined
              }
              commitBlockedReason={commitRowDisabledReason?.(row)}
              commitInFlight={commit?.inFlight}
              onViewDocument={
                onViewDocument && row.resourceType === 'document_reference' && !row.pinned
                  ? () => onViewDocument(row.id)
                  : undefined
              }
              selected={selection?.selectedIds.has(row.id) ?? false}
              onToggleSelect={
                selection
                  ? (next) => selection.onToggle(row.id, next)
                  : undefined
              }
              orgReviewNeeded={orgReview?.flaggedIds.has(row.id) ?? false}
              orgReviewBy={orgReview?.flaggedBy?.[row.id] ?? null}
              onToggleOrgReview={
                orgReview
                  ? (needed) => orgReview.onToggle(row.id, needed)
                  : undefined
              }
              showStatusChip={showStatusChips}
              patientHref={patientHref?.(row)}
              candidateHref={candidateHref}
              resourceHref={resourceHref}
              onOpenStagedRef={onOpenStagedRef}
              showConvertedData={showConvertedData}
              showSourceKey={showSourceKey}
              onResolveFields={
                fieldResolution && isConflict(row) && (row.competing?.length ?? 0) > 0
                  ? (picks) => fieldResolution.onResolve(row.id, picks)
                  : undefined
              }
              plainEmptyConflict={plainEmptyConflictCopy}
            />
          ))
        )}
      </Box>

      {/* Server-driven pagination: filters + paging cover the whole import. */}
      {serverBrowse && serverBrowse.totalRows > serverBrowse.perPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
          <Pagination
            page={serverBrowse.page}
            count={Math.max(
              1,
              Math.ceil(serverBrowse.totalRows / serverBrowse.perPage),
            )}
            onChange={(_, page) => serverBrowse.onPageChange(page)}
            size="small"
          />
        </Box>
      )}

      {/* Commit bar (host may own commit instead) */}
      {showCommitBar && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {openItems > 0
              ? `${openItems} item${openItems > 1 ? 's' : ''} still need a decision before commit.`
              : 'All conflicts and matches resolved — ready to commit.'}
          </Typography>
          <Button
            variant="contained"
            color={openItems > 0 ? 'warning' : 'primary'}
            disabled={openItems > 0}
            onClick={onCommit}
          >
            {commitLabel}
          </Button>
        </Box>
      )}
    </Box>
  );
}
